import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { createSelector } from 'reselect';
import { AppState } from 'state/state.model';
import { OrderTypeState, Row } from 'state/workspaces/secondary-market/batch/model';
import { asSuccessByOrderType } from 'state/workspaces/secondary-market/batch/utils';

export const selectStateByOrderType = (state: AppState, orderType: OrderType): OrderTypeState =>
  state.workspaces.secondaryMarketBatch.stateByOrderType[orderType];

export const selectRowsByOrderType = (state: AppState, orderType: OrderType) =>
  asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.rows ?? EMPTY;

export const selectStatusByOrderType = createSelector(
  (appState: AppState, orderType: OrderType) => selectStateByOrderType(appState, orderType),
  state => state.status,
);

export const selectRowCountByOrderType = createSelector(
  (appState: AppState, orderType: OrderType) => selectStateByOrderType(appState, orderType),
  state => (state.status === 'success' ? state.content.rows.length : undefined),
);

export const selectRowsDataByOrderType = (state: AppState, orderType: OrderType) =>
  asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.rows.map(row => row.data) ?? EMPTY;

export const selectStateStatusByOrderType = (state: AppState, orderType: OrderType) =>
  state.workspaces.secondaryMarketBatch.stateByOrderType[orderType].status;
export const selectOrderType = (state: AppState) => state.workspaces.secondaryMarketBatch.orderType;
export const selectSearchType = (state: AppState, orderType: OrderType) =>
  state.workspaces.secondaryMarketBatch.stateByOrderType[orderType].searchType;

export const selectPreferenceState = (state: AppState, orderType: OrderType) =>
  asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.preference;

const EMPTY = [] as const;
export const selectColumnsDefinitions = (state: AppState, orderType: OrderType) =>
  asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.columnsDefinitions ?? EMPTY;

const getAllRows = (state: AppState, orderType: OrderType): Row[] => {
  return asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.rows ?? [];
};

const getCpOrderSelectedRows = createSelector([(state: AppState) => getAllRows(state, 'cp-order')], rows =>
  rows.filter(row => row.selected),
);

const getEbizQuoteSelectedRows = createSelector([(state: AppState) => getAllRows(state, 'ebiz-quote')], rows =>
  rows.filter(row => row.selected),
);

const getLiveQuoteSelectedRows = createSelector([(state: AppState) => getAllRows(state, 'live-quote')], rows =>
  rows.filter(row => row.selected),
);
export function getSelectedRows(state: AppState, orderType: OrderType): Row[];
export function getSelectedRows(state: AppState, orderType: OrderType) {
  return orderType === 'cp-order'
    ? getCpOrderSelectedRows(state)
    : orderType === 'live-quote'
      ? getLiveQuoteSelectedRows(state)
      : getEbizQuoteSelectedRows(state);
}

export function getSearchByCode(state: AppState) {
  const orderType = selectOrderType(state);
  return asSuccessByOrderType(selectStateByOrderType(state, orderType))?.content.searchByCodes;
}
